import React from "react";
import styled, { keyframes } from "styled-components";
import { IconContainer, Icon, GradientText } from "./styledComponents";

import aiLogo from "../../assets/svg/ai_logo.svg";

// Shiny sweep animation
const shineSwipe = keyframes`
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
`;

/* ============================================
   STASHED: Sparkle animations (for future use)
   ============================================

// import { boxShadow } from "../common/styledComponents";
// const pulseGlow = keyframes`
//   0%, 100% {
//     box-shadow: ${boxShadow}, 0 0 8px rgba(138, 43, 226, 0.3);
//   }
//   50% {
//     box-shadow: ${boxShadow}, 0 0 16px rgba(138, 43, 226, 0.5);
//   }
// `;   
const sparkle1 = keyframes\`
  0%, 100% { opacity: 0; transform: scale(0) translate(0, 0); }
  50% { opacity: 1; transform: scale(1) translate(-8px, -12px); }
\`;

const sparkle2 = keyframes\`
  0%, 100% { opacity: 0; transform: scale(0) translate(0, 0); }
  40% { opacity: 1; transform: scale(1) translate(10px, -8px); }
  80% { opacity: 0; transform: scale(0) translate(10px, -8px); }
\`;

const sparkle3 = keyframes\`
  0%, 100% { opacity: 0; transform: scale(0) translate(0, 0); }
  30% { opacity: 0; transform: scale(0) translate(0, 0); }
  60% { opacity: 1; transform: scale(1) translate(-6px, 10px); }
  90% { opacity: 0; transform: scale(0) translate(-6px, 10px); }
\`;

const sparkle4 = keyframes\`
  0%, 100% { opacity: 0; transform: scale(0) translate(0, 0); }
  20% { opacity: 0; transform: scale(0) translate(0, 0); }
  50% { opacity: 1; transform: scale(1) translate(12px, 6px); }
  80% { opacity: 0; transform: scale(0) translate(12px, 6px); }
\`;

Sparkle CSS classes:
.sparkle {
  position: absolute;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  opacity: 0;
  pointer-events: none;
}
.sparkle-1 { background: #6ef2a6; box-shadow: 0 0 6px 2px #6ef2a6, 0 0 12px 4px rgba(110, 242, 166, 0.6); left: 0; top: 50%; }
.sparkle-2 { background: #32e0c4; box-shadow: 0 0 6px 2px #32e0c4, 0 0 12px 4px rgba(50, 224, 196, 0.6); right: 0; top: 50%; }
.sparkle-3 { background: #2bb3f3; box-shadow: 0 0 6px 2px #2bb3f3, 0 0 12px 4px rgba(43, 179, 243, 0.6); left: 30%; top: 50%; }
.sparkle-4 { background: #3a5cf5; box-shadow: 0 0 6px 2px #3a5cf5, 0 0 12px 4px rgba(58, 92, 245, 0.6); right: 30%; top: 50%; }
.sparkle-5 { background: #fff; box-shadow: 0 0 6px 2px #fff, 0 0 10px 4px rgba(110, 242, 166, 0.5); left: 50%; top: 50%; }
.sparkle-6 { background: #fff; box-shadow: 0 0 6px 2px #fff, 0 0 10px 4px rgba(58, 92, 245, 0.5); left: 15%; top: 50%; }

Hover triggers:
&:hover .sparkle-1 { animation: sparkle1 0.8s ease-in-out infinite; }
&:hover .sparkle-2 { animation: sparkle2 0.8s ease-in-out infinite; }
&:hover .sparkle-3 { animation: sparkle3 0.8s ease-in-out infinite; }
&:hover .sparkle-4 { animation: sparkle4 0.8s ease-in-out infinite; }
&:hover .sparkle-5 { animation: sparkle1 0.9s ease-in-out 0.1s infinite; }
&:hover .sparkle-6 { animation: sparkle3 0.7s ease-in-out 0.2s infinite; }

JSX sparkle elements:
<span className="sparkle sparkle-1" />
<span className="sparkle sparkle-2" />
<span className="sparkle sparkle-3" />
<span className="sparkle sparkle-4" />
<span className="sparkle sparkle-5" />
<span className="sparkle sparkle-6" />
============================================ */

interface AIBadgeProps {
  size?: number;
}

export const AIBadge: React.FC<AIBadgeProps> = ({ size = 35 }) => {
  return (
    <IconContainer size={size} m={0} title="AI Generated" br={50}>
      <Icon src={aiLogo} alt="AI" style={{ borderRadius: "50%" }} />
    </IconContainer>
  );
};

// Logo + Text variant
const BadgeContainer = styled.div<{ $gap?: number }>`
  display: inline-flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  width: fit-content;
  gap: ${({ $gap }): number => $gap ?? 0}px;
`;

const AnimatedGradientText = styled(GradientText)<{ $animateAlways?: boolean }>`
  font-weight: 200;
  font-size: 14px;
  position: relative;
  overflow: hidden;
  margin-left: -8px;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 50%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.6) 50%,
      transparent 100%
    );
    transform: skewX(-20deg);
    ${({ $animateAlways }): string | false | undefined =>
      $animateAlways && `animation: ${shineSwipe} 2s ease-in-out infinite;`}
  }

  ${BadgeContainer}:hover &::after {
    animation: ${shineSwipe} 2s ease-in-out infinite;
  }
`;

interface CompassAIBadgeProps {
  iconSize?: number;
  fontSize?: number;
  animateAlways?: boolean;
  gap?: number;
  fontWeight?: number;
}

export const CompassAIBadge: React.FC<CompassAIBadgeProps> = ({
  iconSize = 32,
  fontSize = 14,
  animateAlways = false,
  gap = 0,
  fontWeight = 200,
}) => {
  return (
    <BadgeContainer title="Compass_ai" $gap={gap}>
      <IconContainer size={iconSize} m={0} p={1} br={50}>
        <Icon src={aiLogo} alt="AI" style={{ borderRadius: "50%" }} />
      </IconContainer>
      <AnimatedGradientText
        $animateAlways={animateAlways}
        style={{ fontSize, fontWeight }}
      >
        Compass_ai
      </AnimatedGradientText>
    </BadgeContainer>
  );
};
